import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  useTheme,
} from "@mui/material";
import {Link} from "react-router-dom"
import { motion } from "framer-motion";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import PieChartOutlineOutlinedIcon from "@mui/icons-material/PieChartOutlineOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";

const MotionBox = motion(Box);

const CustomizeSolution = () => {
  const theme = useTheme();

  // Floating icons animation
  const floatingIcons = [
    { Icon: TrendingUpOutlinedIcon, delay: 0, duration: 3 },
    { Icon: AttachMoneyOutlinedIcon, delay: 0.5, duration: 3.5 },
    { Icon: PieChartOutlineOutlinedIcon, delay: 1, duration: 4 },
    { Icon: BarChartOutlinedIcon, delay: 1.5, duration: 3.2 },
  ];

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
        position: "relative",
        overflow: "hidden",
        minHeight: { xs: "auto", md: "600px" },
      }}
    >
      {/* Animated gradient orbs */}
      <MotionBox
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        sx={{
          position: "absolute",
          top: "10%",
          left: "5%",
          width: "500px",
          height: "500px",
          borderRadius: "50%",
          background: `radial-gradient(circle, ${theme.palette.primary.main}40 0%, transparent 70%)`,
          filter: "blur(60px)",
          pointerEvents: "none",
        }}
      />

      <MotionBox
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.2, 0.4, 0.2],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        sx={{
          position: "absolute",
          bottom: "5%",
          right: "10%",
          width: "450px",
          height: "450px",
          borderRadius: "50%",
          background: `radial-gradient(circle, ${theme.palette.secondary.main}35 0%, transparent 70%)`,
          filter: "blur(70px)",
          pointerEvents: "none",
        }}
      />

      {/* Floating financial icons */}
      {floatingIcons.map((item, index) => {
        const Icon = item.Icon;
        return (
          <MotionBox
            key={index}
            animate={{
              y: [0, -30, 0],
              x: [0, 15, 0],
              rotate: [0, 10, 0],
              opacity: [0.2, 0.4, 0.2],
            }}
            transition={{
              duration: item.duration,
              delay: item.delay,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            sx={{
              position: "absolute",
              left: `${15 + index * 20}%`,
              top: `${20 + index * 15}%`,
              pointerEvents: "none",
              display: { xs: "none", md: "block" },
            }}
          >
            <Icon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
          </MotionBox>
        );
      })}

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
          {/* Left Side - Heading with Animation */}
          <Grid item xs={12} md={6}>
            <MotionBox
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <Typography
                variant="overline"
                sx={{
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                  letterSpacing: 2,
                  mb: 2,
                  display: "block",
                }}
              >
                FINANCIAL EXCELLENCE
              </Typography>

              <Typography
                variant="h1"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "2.5rem", md: "3.5rem", lg: "4rem" },
                  lineHeight: 1.2,
                  mb: 3,
                  background: `linear-gradient(135deg, ${theme.palette.text.primary} 0%, ${theme.palette.primary.main} 100%)`,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  position: "relative",
                }}
              >
                Customized Financial Solutions
                <MotionBox
                  initial={{ width: 0 }}
                  whileInView={{ width: "100%" }}
                  viewport={{ once: true }}
                  transition={{ duration: 1, delay: 0.5 }}
                  sx={{
                    height: "4px",
                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    borderRadius: "2px",
                    mt: 2,
                  }}
                />
              </Typography>

              <MotionBox
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 500,
                    color: theme.palette.primary.main,
                    fontSize: { xs: "1.5rem", md: "2rem" },
                  }}
                >
                  for Your Business
                </Typography>
              </MotionBox>

              {/* Animated decorative elements */}
              <MotionBox
                animate={{
                  rotate: 360,
                }}
                transition={{
                  duration: 20,
                  repeat: Infinity,
                  ease: "linear",
                }}
                sx={{
                  position: "absolute",
                  left: "-50px",
                  top: "50%",
                  width: "100px",
                  height: "100px",
                  border: `2px dashed ${theme.palette.primary.main}30`,
                  borderRadius: "50%",
                  display: { xs: "none", md: "block" },
                }}
              />
            </MotionBox>
          </Grid>

          {/* Right Side - Content and CTA */}
          <Grid item xs={12} md={6}>
            <MotionBox
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
              sx={{
                p: { xs: 3, md: 5 },
                borderRadius: 4,
                background: `linear-gradient(135deg, ${theme.palette.background.paper}95, ${theme.palette.background.default}95)`,
                backdropFilter: "blur(10px)",
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: `0 8px 32px ${theme.palette.primary.main}10`,
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Card decorative element */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  width: "150px",
                  height: "150px",
                  background: `radial-gradient(circle, ${theme.palette.primary.main}15 0%, transparent 70%)`,
                  borderRadius: "0 0 0 100%",
                  pointerEvents: "none",
                }}
              />

              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: theme.palette.text.primary,
                }}
              >
                At f-ai, we offer a range of financial services to help you
                manage your financial records and reporting requirements.
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.text.secondary,
                  lineHeight: 1.8,
                  mb: 3,
                }}
              >
                From bookkeeping to financial planning, we have you covered. Let
                us help you take your business to the next level.
              </Typography>

              {/* Feature highlights */}
              <Box sx={{ mb: 4 }}>
                {[
                  "Bookkeeping & Accounting",
                  "Financial Planning",
                  "Tax Compliance",
                  "Business Advisory",
                ].map((item, index) => (
                  <MotionBox
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        mr: 2,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontWeight: 500,
                      }}
                    >
                      {item}
                    </Typography>
                  </MotionBox>
                ))}
              </Box>

              {/* CTA Button */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  component={Link}
                  to="/Contactus"
                  variant="contained"
                  size="large"
                  sx={{
                    py: 1.5,
                    px: 4,
                    fontSize: "1rem",
                    fontWeight: 600,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    boxShadow: `0 8px 24px ${theme.palette.primary.main}40`,
                    textTransform: "none",
                    borderRadius: 2,
                    "&:hover": {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                      boxShadow: `0 12px 32px ${theme.palette.primary.main}50`,
                    },
                  }}
                >
                  Learn More
                </Button>
              </motion.div>
            </MotionBox>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default CustomizeSolution;
